import { IStatusConfig } from '../../../components/StatusBadge'
import {
  IScheduleClass,
  TScheduleCategory,
  TScheduleCourseType,
  TScheduleFormat,
  TScheduleShift,
  TScheduleStatus,
  TScheduleTerm,
  TScheduleWeekDay
} from '../../../services/ScheduleClasses/interfaces'
import { magenta } from '../../../theme'

/**
 * Casa única dos rótulos em português deste módulo (ABRA-181).
 * Espelha as consts do model ScheduleClass na API — não espalhar switch de rótulo pelos componentes.
 */

export const TERMS: TScheduleTerm[] = ['january_break', 'first_semester', 'july_break', 'second_semester']

export const TERM_LABELS: Record<TScheduleTerm, string> = {
  january_break: 'Férias de Janeiro',
  first_semester: '1º Semestre',
  july_break: 'Férias de Julho',
  second_semester: '2º Semestre'
}

export const SHIFT_LABELS: Record<TScheduleShift, string> = {
  morning: 'Matutino',
  afternoon: 'Vespertino',
  night: 'Noturno',
  full_day: 'Diurno'
}

/** Versão curta para caber na coluna Turno da tabela. */
export const SHIFT_SHORT_LABELS: Record<TScheduleShift, string> = {
  morning: 'Mat.',
  afternoon: 'Vesp.',
  night: 'Not.',
  full_day: 'Diurno'
}

/** De segunda a domingo: é a ordem dos botões do filtro "Dias" e a do valor que ele envia. */
export const WEEK_DAYS: TScheduleWeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]

/** Por extenso: é o nome acessível de cada botão do filtro "Dias". */
export const WEEK_DAY_LABELS: Record<TScheduleWeekDay, string> = {
  monday: 'Segunda',
  tuesday: 'Terça',
  wednesday: 'Quarta',
  thursday: 'Quinta',
  friday: 'Sexta',
  saturday: 'Sábado',
  sunday: 'Domingo'
}

/** Versão curta, o texto dos botões do filtro "Dias", como SHIFT_SHORT_LABELS. */
export const WEEK_DAY_SHORT_LABELS: Record<TScheduleWeekDay, string> = {
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
  sunday: 'Dom'
}

export const FORMAT_LABELS: Record<TScheduleFormat, string> = {
  in_person: 'Presencial',
  live_online: 'Online Ao Vivo',
  hybrid: 'Híbrido'
}

/** Versão curta para caber na coluna Formato da tabela, como SHIFT_SHORT_LABELS. */
export const FORMAT_SHORT_LABELS: Record<TScheduleFormat, string> = {
  in_person: 'Pres.',
  live_online: 'Online',
  hybrid: 'Híbrido'
}

export const CATEGORY_LABELS: Record<TScheduleCategory, string> = {
  free_courses: 'Cursos Livres',
  di_module_year_1: 'Módulo DI 1º Ano',
  di_module_year_2: 'Módulo DI 2º Ano'
}

/**
 * Status da turma. Rótulo e cor no mesmo registro, na forma que o `<StatusBadge>` lê: eram dois
 * `Record` paralelos, que é exatamente como as cópias de status do painel foram divergindo.
 *
 * Os rótulos concordam com "turma" (ABRA-315) — eram masculinos, ao lado de "Nenhuma turma
 * encontrada" e "Excluir turma" — e são os mesmos da página pública. "Retirado do site" saiu
 * junto da importação de planilha que o produzia: era visibilidade, não estado da turma.
 *
 * Dois deles o backend também decide sozinho: `confirmed` a partir de metade das vagas e
 * `sold_out` quando elas acabam (ScheduleClass::statusFor). Continuam na lista porque a
 * pessoa pode antecipá-los; o que ela não consegue é desfazê-los pelo menu.
 */
export const SCHEDULE_STATUS: Record<TScheduleStatus, IStatusConfig> = {
  confirmed: { label: 'Confirmada', colorScheme: 'green' },
  pending: { label: 'Pendente', colorScheme: 'yellow' },
  canceled: { label: 'Cancelada', colorScheme: 'red' },
  extended: { label: 'Prorrogada', colorScheme: 'blue' },
  sold_out: { label: 'Vagas Esgotadas', colorScheme: 'purple' }
}

export const COURSE_TYPE_LABELS: Record<TScheduleCourseType, string> = {
  standard: 'Curso Padrão',
  vacation: 'Curso de Férias'
}

/** Cor da marca aplicada quando o curso não tem cor própria. */
export const DEFAULT_THEME_COLOR = magenta[500]

export const DEFAULT_MAX_STUDENTS = 10

/**
 * Faixas de mês de cada período do ano.
 *
 * Aqui o corte só decide qual aba abre e o que o cabeçalho do formulário promete enquanto a
 * pessoa digita a data. Quem classifica de verdade é o servidor, com o mesmo corte
 * (ScheduleClass::termFor) — mexer aqui pede mexer lá.
 */
export function termForMonth(month: number): TScheduleTerm {
  if (month === 1) return 'january_break'
  if (month === 7) return 'july_break'
  if (month <= 6) return 'first_semester'

  return 'second_semester'
}

/** Aba aberta por padrão, escolhida pelo mês corrente. */
export function currentTermIndex(): number {
  return TERMS.indexOf(termForMonth(new Date().getMonth() + 1))
}

/**
 * O que a coluna Professor mostra quando não há professor vinculado (ABRA-315).
 *
 * A célula era um travessão, igual à de sala vazia e à de formato ausente, e uma turma sem
 * professor não é um campo em branco: é uma pendência. "A Definir" é o que o cronograma do
 * WordPress escrevia, e é o que a secretaria diz ao aluno que pergunta.
 */
export const TEACHER_PLACEHOLDER = 'A Definir'

/**
 * Motivo do aviso na coluna Curso, ou null quando o vínculo está de pé.
 *
 * Espelha ScheduleClass::getIsIncompleteAttribute na API, curso arquivado incluído: courses usa
 * soft delete, então excluir um curso não desfaz o vínculo — ele continua apontando para um
 * cadastro que saiu do ar. Sem este caso o aviso apareceria sem explicação numa linha cujo
 * curso está visivelmente lá, e a pessoa procuraria o problema no lugar errado.
 */
export function courseIssue(scheduleClass: IScheduleClass): string | null {
  if (scheduleClass.course_id === null) return 'Curso não vinculado'
  if (scheduleClass.course?.deleted_at) return 'Curso arquivado'

  return null
}

/**
 * Motivo do aviso na coluna Professor, ou null quando há professor.
 *
 * Era um aviso só, na coluna Curso, dizendo as duas coisas de uma vez ("Curso e professor não
 * vinculados"): o ícone ficava longe da célula que estava vazia, e quem varria a coluna de
 * professores atrás das turmas sem professor não tinha o que varrer.
 */
export function teacherIssue(scheduleClass: IScheduleClass): string | null {
  return scheduleClass.teacher_id === null ? 'Professor não vinculado' : null
}
